import type React from 'react';
import {
  LavashIcon, GataIcon, BeerIcon, DolmaIcon, CoffeeMountainIcon, HorseIcon,
  SkewerIcon, WineIcon, MountainIcon, RopewayIcon, FishIcon, QuillIcon,
  DonutIcon, SoupIcon, BrandyIcon, CandyIcon, HotelIcon, MeatDishIcon,
  HerbBreadIcon, ZiplineIcon, TentIcon, CornIcon, CheeseIcon, KhachkarIcon,
  DressIcon, CarpetIcon, DriedFruitsIcon, FoodIcon, WineryIcon, ActivityIcon,
  DefaultIcon,
  type IconProps,
} from './icons';

type IconComponent = (p: IconProps) => React.ReactElement;

// Keyword → icon. The first entry whose keywords ALL appear in the
// normalized stamp/location name wins. Order matters: most specific first.
const RULES: { keywords: string[]; icon: IconComponent }[] = [
  { keywords: ['lavash'], icon: LavashIcon },
  { keywords: ['gata'], icon: GataIcon },
  { keywords: ['beer'], icon: BeerIcon },
  { keywords: ['dolma'], icon: DolmaIcon },
  { keywords: ['coffee'], icon: CoffeeMountainIcon },
  { keywords: ['horse'], icon: HorseIcon },
  { keywords: ['tandir'], icon: SkewerIcon },
  { keywords: ['xorovac'], icon: SkewerIcon },
  { keywords: ['wine'], icon: WineIcon },
  { keywords: ['areni'], icon: WineIcon },
  { keywords: ['xustup'], icon: MountainIcon },
  { keywords: ['ropeway'], icon: RopewayIcon },
  { keywords: ['tatev'], icon: RopewayIcon },
  { keywords: ['ishxan'], icon: FishIcon },
  { keywords: ['varem', 'marem'], icon: QuillIcon },
  { keywords: ['ponchik'], icon: DonutIcon },
  { keywords: ['xash'], icon: SoupIcon },
  { keywords: ['kognak'], icon: BrandyIcon },
  { keywords: ['ararat', 'noy'], icon: BrandyIcon },
  { keywords: ['sweety'], icon: CandyIcon },
  { keywords: ['yeremyan'], icon: CandyIcon },
  { keywords: ['hotel'], icon: HotelIcon },
  { keywords: ['tufenkian'], icon: HotelIcon },
  { keywords: ['qyalla'], icon: MeatDishIcon },
  { keywords: ['jengyalov'], icon: HerbBreadIcon },
  { keywords: ['zeepline'], icon: ZiplineIcon },
  { keywords: ['zipline'], icon: ZiplineIcon },
  { keywords: ['enoqavan'], icon: ZiplineIcon },
  { keywords: ['enokavan'], icon: ZiplineIcon },
  { keywords: ['overnight'], icon: TentIcon },
  { keywords: ['eghegis'], icon: TentIcon },
  { keywords: ['corn'], icon: CornIcon },
  { keywords: ['cheese'], icon: CheeseIcon },
  { keywords: ['khachqar'], icon: KhachkarIcon },
  { keywords: ['khachkar'], icon: KhachkarIcon },
  { keywords: ['dress'], icon: DressIcon },
  { keywords: ['carpet'], icon: CarpetIcon },
  { keywords: ['dried', 'fruit'], icon: DriedFruitsIcon },
  { keywords: ['fruit'], icon: DriedFruitsIcon },
];

const CATEGORY_FALLBACK: Record<string, IconComponent> = {
  food: FoodIcon,
  winery: WineryIcon,
  activity: ActivityIcon,
};

const normalize = (s: string) =>
  s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9 ]/g, ' ');

/**
 * Resolve the best icon for a stamp. Matches on the stamp name and
 * location name (both checked), falling back to the location category,
 * then a generic mark.
 */
export function resolveStampIcon(
  name: string,
  locationName?: string,
  category?: string,
): IconComponent {
  const haystack = normalize(`${name} ${locationName ?? ''}`);

  for (const rule of RULES) {
    if (rule.keywords.every((k) => haystack.includes(k))) {
      return rule.icon;
    }
  }

  if (category) {
    const fallback = CATEGORY_FALLBACK[category.toLowerCase()];
    if (fallback) return fallback;
  }

  return DefaultIcon;
}
